import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  doc,
  getDoc,
  setDoc,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { getInventoryAdjustments, InventoryAdjustment } from './inventoryAdjustmentServices';
import { getPurchaseReceipts, PurchaseReceipt } from './inventoryCostServices';

// Interface for product COGS data
export interface ProductCOGS {
  productId: string;
  productName: string;
  productSku: string;
  beginningStock: number;
  beginningValue: number;
  purchaseQuantity: number;
  purchaseValue: number;
  manualAdditions: number;
  manualReductions: number;
  salesQuantity: number;
  endingStock: number;
  endingValue: number;
  averageCost: number;
  writeOffCost: number;
  totalCOGS: number;
  hasUnpostedGLTransactions?: boolean; // Flag to indicate if there are unposted GL transactions
  purchaseReceiptIds?: string[]; // IDs of purchase receipts for this product in the current month
}

// Interface for monthly inventory state
export interface MonthlyInventoryState {
  id?: string;
  year: number;
  month: number;
  products: ProductCOGS[];
  totals: {
    beginningValue: number;
    purchaseValue: number;
    endingValue: number;
    totalCOGS: number;
  };
  glTransactionId?: string; // ID of the GL transaction for this monthly state
  createdAt?: Date | Timestamp | any; // Allow for serverTimestamp()
  updatedAt?: Date | Timestamp | any; // Allow for serverTimestamp()
}

// Get or calculate monthly inventory state
export const getMonthlyInventoryState = async (
  year: number,
  month: number,
  forceCalculate: boolean = false
): Promise<MonthlyInventoryState> => {
  try {
    // Check if we already have calculated this month's state
    const stateId = `${year}-${month}`;
    const stateRef = doc(db, 'monthlyInventoryState', stateId);
    const stateSnap = await getDoc(stateRef);
    
    if (stateSnap.exists() && !forceCalculate) {
      // Return the cached state if it exists and we're not forcing a recalculation
      return {
        id: stateSnap.id,
        ...stateSnap.data()
      } as MonthlyInventoryState;
    }
    
    // If not, calculate the state
    return calculateMonthEndInventory(year, month);
  } catch (error) {
    console.error('Error getting monthly inventory state:', error);
    throw error;
  }
};

// Calculate month-end inventory state
export const calculateMonthEndInventory = async (
  year: number,
  month: number
): Promise<MonthlyInventoryState> => {
  try {
    // Step 1: Get all active products
    const productsQuery = query(collection(db, 'products'), where('active', '==', true));
    const productsSnapshot = await getDocs(productsQuery);
    const products: Array<any> = [];
    
    productsSnapshot.forEach((doc) => {
      products.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Step 2: Get previous month's inventory state
    const previousMonth = month === 0 ? 11 : month - 1;
    const previousYear = month === 0 ? year - 1 : year;
    let previousState: MonthlyInventoryState | null = null;
    
    try {
      // Check if previous month state exists without recursively calculating
      const prevStateId = `${previousYear}-${previousMonth}`;
      const prevStateRef = doc(db, 'monthlyInventoryState', prevStateId);
      const prevStateSnap = await getDoc(prevStateRef);
      
      if (prevStateSnap.exists()) {
        const prevData = prevStateSnap.data();
        previousState = {
          id: prevStateSnap.id,
          year: prevData.year,
          month: prevData.month,
          products: prevData.products || [],
          totals: prevData.totals || {
            beginningValue: 0,
            purchaseValue: 0,
            endingValue: 0,
            totalCOGS: 0
          },
          createdAt: prevData.createdAt,
          updatedAt: prevData.updatedAt
        };
        
        console.log(`Found previous month state for ${previousYear}-${previousMonth} with ${previousState.products.length} products`);
      } else {
        console.warn(`No previous month state found for ${previousYear}-${previousMonth}`);
      }
    } catch (error) {
      console.error('Error fetching previous month state:', error);
      console.warn('No previous month state found, starting fresh');
    }
    
    // Step 3: Get all inventory movements for the current month
    // Define date range for the current month
    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0, 23, 59, 59, 999); // Last day of month
    
    // Get purchase receipts
    // Note: The current implementation of getPurchaseReceipts doesn't support date range filtering
    // We'll get all receipts and filter them manually
    const purchaseReceipts = await getPurchaseReceipts(1000); // Get a large number of receipts
    
    // Filter receipts for the current month
    const currentMonthReceipts = purchaseReceipts.filter(receipt => {
      const receiptDate = receipt.receiptDate instanceof Date 
        ? receipt.receiptDate 
        : new Date((receipt.receiptDate as any).seconds * 1000);
      return receiptDate >= startDate && receiptDate <= endDate;
    });
    
    // Get inventory adjustments for the month
    const adjustments = await getInventoryAdjustments(2000); // Get a large number of adjustments
    
    // Filter adjustments for the current month
    const currentMonthAdjustments = adjustments.filter(adj => {
      const adjDate = adj.adjustmentDate instanceof Date 
        ? adj.adjustmentDate 
        : new Date((adj.adjustmentDate as any).seconds * 1000);
      return adjDate >= startDate && adjDate <= endDate;
    });
    
    // Step 4: Initialize product COGS map
    const productCOGSMap = new Map<string, ProductCOGS>();
    
    // Initialize with all products
    products.forEach(product => {
      const productId = product.id;
      if (!productId) return; // Skip products without an ID
      
      // Find previous state for this product
      const previousProductState = previousState?.products.find(p => p.productId === productId);
      
      // Log for debugging
      if (previousProductState) {
        console.log(`Previous state for product ${product.name}: ending stock = ${previousProductState.endingStock}, ending value = ${previousProductState.endingValue}`);
      }
      
      productCOGSMap.set(productId, {
        productId,
        productName: product.name || 'Unknown Product',
        productSku: product.sku || '',
        beginningStock: previousProductState?.endingStock || 0,
        beginningValue: previousProductState?.endingValue || 0,
        purchaseQuantity: 0,
        purchaseValue: 0,
        manualAdditions: 0,
        manualReductions: 0,
        salesQuantity: 0,
        endingStock: previousProductState?.endingStock || 0, // Initialize with beginning stock
        endingValue: previousProductState?.endingValue || 0, // Initialize with beginning value
        averageCost: previousProductState?.averageCost || 0,
        writeOffCost: 0,
        totalCOGS: 0,
        hasUnpostedGLTransactions: false,
        purchaseReceiptIds: []
      });
    });
    
    // Also check for products that exist in previous state but not in current products list
    if (previousState && previousState.products) {
      previousState.products.forEach(prevProduct => {
        if (!productCOGSMap.has(prevProduct.productId)) {
          console.log(`Adding product from previous month that's not in current products list: ${prevProduct.productName}`);
          
          productCOGSMap.set(prevProduct.productId, {
            productId: prevProduct.productId,
            productName: prevProduct.productName,
            productSku: prevProduct.productSku,
            beginningStock: prevProduct.endingStock,
            beginningValue: prevProduct.endingValue,
            purchaseQuantity: 0,
            purchaseValue: 0,
            manualAdditions: 0,
            manualReductions: 0,
            salesQuantity: 0,
            endingStock: prevProduct.endingStock, // Initialize with previous ending stock
            endingValue: prevProduct.endingValue, // Initialize with previous ending value
            averageCost: prevProduct.averageCost,
            writeOffCost: 0,
            totalCOGS: 0,
            hasUnpostedGLTransactions: false,
            purchaseReceiptIds: []
          });
        }
      });
    }
    
    // Step 5: Process purchase receipts
    currentMonthReceipts.forEach(receipt => {
      receipt.items?.forEach(item => {
        const productId = item.productId;
        let productCOGS = productCOGSMap.get(productId);
        
        if (!productCOGS) {
          // This is a product that exists in transactions but not in the product database
          productCOGS = {
            productId,
            productName: item.productName,
            productSku: item.productSku || '',
            beginningStock: 0,
            beginningValue: 0,
            purchaseQuantity: 0,
            purchaseValue: 0,
            manualAdditions: 0,
            manualReductions: 0,
            salesQuantity: 0,
            endingStock: 0,
            endingValue: 0,
            averageCost: 0,
            writeOffCost: 0,
            totalCOGS: 0,
            hasUnpostedGLTransactions: false,
            purchaseReceiptIds: []
          };
          productCOGSMap.set(productId, productCOGS);
        }
        
        // Track purchase receipt IDs
        if (receipt.id && !productCOGS.purchaseReceiptIds?.includes(receipt.id)) {
          productCOGS.purchaseReceiptIds = [...(productCOGS.purchaseReceiptIds || []), receipt.id];
          
          // Check if this receipt has unposted GL transactions
          if (!receipt.glTransactionId) {
            productCOGS.hasUnpostedGLTransactions = true;
          }
        }
        
        // Update purchase quantity and value
        productCOGS.purchaseQuantity += item.quantity;
        // Use item.unitCost or a default price if unitPrice doesn't exist
        const unitPrice = (item as any).unitPrice || item.unitCost || 0;
        productCOGS.purchaseValue += item.quantity * unitPrice;
      });
    });
    
    // Step 6: Process inventory adjustments
    currentMonthAdjustments.forEach(adjustment => {
      adjustment.items.forEach(item => {
        const productId = item.productId;
        let productCOGS = productCOGSMap.get(productId);
        
        if (!productCOGS) {
          // This is a product that exists in transactions but not in the product database
          productCOGS = {
            productId,
            productName: item.productName,
            productSku: item.productSku || '',
            beginningStock: 0,
            beginningValue: 0,
            purchaseQuantity: 0,
            purchaseValue: 0,
            manualAdditions: 0,
            manualReductions: 0,
            salesQuantity: 0,
            endingStock: 0,
            endingValue: 0,
            averageCost: 0,
            writeOffCost: 0,
            totalCOGS: 0
          };
          productCOGSMap.set(productId, productCOGS);
        }
        
        const quantityChange = item.quantityChange;
        const subtype = item.subtype || '';
        const reason = adjustment.reason.toLowerCase();
        
        if (subtype === 'sale_deduction' || reason.includes('sale:')) {
          // Sales movement
          productCOGS.salesQuantity += Math.abs(quantityChange);
        } else if (!(subtype === 'purchase_receipt' || reason.includes('purchase receipt'))) {
          // Manual adjustment (not purchase receipt)
          if (quantityChange > 0) {
            productCOGS.manualAdditions += quantityChange;
          } else {
            productCOGS.manualReductions += Math.abs(quantityChange);
          }
        }
        // Note: Purchase receipts are already handled separately
      });
    });
    
    // Step 7: Calculate month-end average cost, ending stock, and COGS
    productCOGSMap.forEach(productCOGS => {
      // Calculate net manual adjustment (additions - reductions)
      const netManualAdjustment = productCOGS.manualAdditions - productCOGS.manualReductions;
      
      // Calculate total units considering all movements
      const totalUnits = productCOGS.beginningStock + productCOGS.purchaseQuantity;
      
      // Calculate total value (only beginning value and purchase value contribute to total value)
      const totalValue = productCOGS.beginningValue + productCOGS.purchaseValue;
      
      // Calculate month-end average cost
      productCOGS.averageCost = totalUnits > 0 ? totalValue / totalUnits : 0;
      
      // Calculate COGS for sales only - manual adjustments are separate
      productCOGS.totalCOGS = productCOGS.salesQuantity * productCOGS.averageCost;
      
      // Calculate write-off cost (for manual adjustments) - not included in COGS
      // Only negative net adjustments (more reductions than additions) count as write-offs
      productCOGS.writeOffCost = netManualAdjustment < 0 ? 
        Math.abs(netManualAdjustment) * productCOGS.averageCost : 0;
      
      // Calculate ending stock
      productCOGS.endingStock = totalUnits + netManualAdjustment - productCOGS.salesQuantity;
      
      // Calculate ending value
      productCOGS.endingValue = productCOGS.endingStock * productCOGS.averageCost;
      
      console.log(`COGS calculation for ${productCOGS.productName}:`);
      console.log(`  Beginning: ${productCOGS.beginningStock} units at ${productCOGS.beginningValue}`);
      console.log(`  Purchases: ${productCOGS.purchaseQuantity} units at ${productCOGS.purchaseValue}`);
      console.log(`  Total units for avg cost: ${totalUnits} units`);
      console.log(`  Total value for avg cost: ${totalValue}`);
      console.log(`  Average cost: ${productCOGS.averageCost}`);
      console.log(`  Manual adjustments: +${productCOGS.manualAdditions}, -${productCOGS.manualReductions}, net ${netManualAdjustment}`);
      console.log(`  Write-off cost: ${productCOGS.writeOffCost}`);
      console.log(`  Sales: ${productCOGS.salesQuantity} units`);
      console.log(`  Sales COGS: ${productCOGS.totalCOGS}`);
      console.log(`  Ending stock: ${productCOGS.endingStock} units`);
      console.log(`  Ending value: ${productCOGS.endingValue}`);
    });
    
    // Step 8: Create the monthly inventory state object
    const productCOGSArray = Array.from(productCOGSMap.values());
    
    const totals = {
      beginningValue: productCOGSArray.reduce((sum, item) => sum + item.beginningValue, 0),
      purchaseValue: productCOGSArray.reduce((sum, item) => sum + item.purchaseValue, 0),
      endingValue: productCOGSArray.reduce((sum, item) => sum + item.endingValue, 0),
      totalCOGS: productCOGSArray.reduce((sum, item) => sum + item.totalCOGS, 0)
    };
    
    const monthlyState: MonthlyInventoryState = {
      year,
      month,
      products: productCOGSArray,
      totals,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    // Step 9: Save the calculated state to Firestore for future use
    const stateId = `${year}-${month}`;
    
    // Create a copy of the state without Date objects for Firestore
    const firestoreState = {
      ...monthlyState,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(doc(db, 'monthlyInventoryState', stateId), firestoreState);
    
    // Return the calculated state
    return {
      id: stateId,
      ...monthlyState
    };
  } catch (error) {
    console.error('Error calculating month-end inventory:', error);
    throw error;
  }
};

// Recalculate monthly inventory state (force refresh)
export const recalculateMonthlyInventoryState = async (
  year: number,
  month: number
): Promise<MonthlyInventoryState> => {
  try {
    return await calculateMonthEndInventory(year, month);
  } catch (error) {
    console.error('Error recalculating monthly inventory state:', error);
    throw error;
  }
};

// Get monthly inventory state by ID
export const getMonthlyInventoryStateById = async (id: string): Promise<MonthlyInventoryState | null> => {
  try {
    const stateRef = doc(db, 'monthlyInventoryState', id);
    const stateSnap = await getDoc(stateRef);
    
    if (stateSnap.exists()) {
      return {
        id: stateSnap.id,
        ...stateSnap.data()
      } as MonthlyInventoryState;
    }
    
    return null;
  } catch (error) {
    console.error('Error getting monthly inventory state by ID:', error);
    throw error;
  }
};

// Update monthly inventory state with GL transaction ID
export const updateMonthlyInventoryStateWithGLTransaction = async (stateId: string, glTransactionId: string): Promise<void> => {
  try {
    const stateRef = doc(db, 'monthlyInventoryState', stateId);
    await setDoc(stateRef, {
      glTransactionId: glTransactionId,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating monthly inventory state with GL transaction ID:', error);
    throw error;
  }
};

// Remove GL transaction ID from monthly inventory state
export const removeMonthlyInventoryStateGLTransaction = async (stateId: string): Promise<void> => {
  try {
    const stateRef = doc(db, 'monthlyInventoryState', stateId);
    await setDoc(stateRef, {
      glTransactionId: null,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error removing GL transaction ID from monthly inventory state:', error);
    throw error;
  }
};
